import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';

const SIX_MONTHS_MS = 1000 * 60 * 60 * 24 * 180;

const useBabyPrompt = () => {
  const [profile, setProfile] = useState(null);
  const [showBabyModal, setShowBabyModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback((extra = {}) => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };
  }, []);

  const shouldShowModal = useCallback((p) => {
    if (!p) return false;
    const hasBabyInfo = Boolean(p.babyBirthDate || p.babyAgeGroup);
    if (hasBabyInfo) return false;
    const lastShownTs = p.lastModalShown ? new Date(p.lastModalShown).getTime() : null;
    const now = Date.now();
    if (lastShownTs && now - lastShownTs < SIX_MONTHS_MS) return false;
    return true;
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const me = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include', headers: getAuthHeaders() });
      if (!me.ok) {
        setProfile(null);
        setShowBabyModal(false);
        setLoading(false);
        return null;
      }

      const res = await fetch(`${API_BASE_URL}/api/profile`, { credentials: 'include', headers: getAuthHeaders() });
      if (!res.ok) {
        setProfile(null);
        setShowBabyModal(false);
        setLoading(false);
        return null;
      }

      const p = await res.json();
      setProfile(p);
      setShowBabyModal(shouldShowModal(p));
      setLoading(false);
      return p;
    } catch (error) {
      console.error('useBabyPrompt refreshProfile error', error);
      setProfile(null);
      setShowBabyModal(false);
      setLoading(false);
      return null;
    }
  }, [getAuthHeaders, shouldShowModal]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const markModalShown = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/api/profile/update-modal-shown`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' })
      });
    } catch (error) {
      console.warn('markModalShown failed', error);
    }
    setProfile((prev) => (prev ? { ...prev, lastModalShown: new Date().toISOString() } : prev));
  }, [getAuthHeaders]);

  const submitBabyData = useCallback(async (babyData) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile/update-baby-preferences`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(babyData)
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setProfile((prev) => ({ ...(prev || {}), ...(json && json.profile ? json.profile : {}), ...babyData }));
        setShowBabyModal(false);
        await markModalShown();
        // Dispatch global event so components like TopBar can refresh
        window.dispatchEvent(new CustomEvent('profileUpdated'));
        return true;
      }
      console.error('submitBabyData failed', json);
      return false;
    } catch (error) {
      console.error('submitBabyData error', error);
      return false;
    }
  }, [getAuthHeaders, markModalShown]);

  const closeBabyModal = useCallback(async () => {
    setShowBabyModal(false);
    await markModalShown();
  }, [markModalShown]);

  const openBabyModal = useCallback(() => {
    setShowBabyModal(true);
  }, []);

  return {
    profile,
    showBabyModal,
    loading,
    submitBabyData,
    closeBabyModal,
    openBabyModal,
    refreshProfile
  };
};

export default useBabyPrompt;
